import React, { useState, useEffect, useMemo } from 'react';
import { MessageSquare, User, Clock, ChevronLeft, Trash2, Search, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { API } from '@/config';

const t = (key, lang) => {
  const tr = {
    title: { ar: 'محادثات المستخدمين', sv: 'Användarkonversationer', en: 'User Conversations' },
    totalMessages: { ar: 'إجمالي الرسائل', sv: 'Totalt meddelanden', en: 'Total Messages' },
    users: { ar: 'المستخدمين', sv: 'Användare', en: 'Users' },
    messages: { ar: 'رسالة', sv: 'meddelanden', en: 'messages' },
    lastMessage: { ar: 'آخر رسالة', sv: 'Senaste', en: 'Last message' },
    viewChat: { ar: 'عرض المحادثة', sv: 'Visa chatt', en: 'View Chat' },
    back: { ar: 'رجوع', sv: 'Tillbaka', en: 'Back' },
    userMessage: { ar: 'المستخدم', sv: 'Användare', en: 'User' },
    aiResponse: { ar: 'الرد', sv: 'AI-svar', en: 'AI Response' },
    deleteAll: { ar: 'حذف الكل', sv: 'Radera alla', en: 'Delete All' },
    deleteUser: { ar: 'حذف محادثات هذا المستخدم', sv: 'Radera denna användares chattar', en: 'Delete this user\'s chats' },
    confirmDelete: { ar: 'هل أنت متأكد من الحذف؟', sv: 'Är du säker?', en: 'Are you sure?' },
    noMessages: { ar: 'لا توجد محادثات', sv: 'Inga konversationer', en: 'No conversations' },
    search: { ar: 'بحث...', sv: 'Sök...', en: 'Search...' },
  };
  return tr[key]?.[lang] || tr[key]?.en || key;
};

const timeAgo = (dateStr, lang) => {
  if (!dateStr) return '-';
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return lang === 'ar' ? 'الآن' : 'Just nu';
  if (diff < 3600) return `${Math.floor(diff / 60)} ${lang === 'ar' ? 'د' : 'min'}`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} ${lang === 'ar' ? 'س' : 'h'}`;
  return `${Math.floor(diff / 86400)} ${lang === 'ar' ? 'يوم' : 'd'}`;
};

const ChatMessagesManagement = ({ token, i18n }) => {
  const lang = i18n?.language || 'ar';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [search, setSearch] = useState('');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchData = async (userId = null) => {
    setLoading(true);
    try {
      const url = userId
        ? `${API}/admin/chat-messages?user_id=${userId}&per_page=200`
        : `${API}/admin/chat-messages?per_page=200`;
      const res = await fetch(url, { headers });
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [token]);

  const handleDelete = async (userId = null) => {
    if (!window.confirm(t('confirmDelete', lang))) return;
    try {
      const url = userId
        ? `${API}/admin/chat-messages?user_id=${userId}`
        : `${API}/admin/chat-messages`;
      const res = await fetch(url, { method: 'DELETE', headers });
      if (res.ok) {
        const d = await res.json();
        toast.success(`${d.deleted} deleted`);
        if (userId && selectedUser) { setSelectedUser(null); }
        fetchData();
      }
    } catch (e) { console.error(e); }
  };

  const openUser = (userId) => {
    setSelectedUser(userId);
    fetchData(userId);
  };

  const goBack = () => {
    setSelectedUser(null);
    fetchData();
  };

  if (loading && !data) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div></div>;

  // User conversation view
  if (selectedUser && data) {
    const userName = data.users?.find(u => u.user_id === selectedUser)?.name || selectedUser.slice(0, 12);
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={goBack} data-testid="chat-back-btn"><ChevronLeft className="w-4 h-4" />{t('back', lang)}</Button>
            <h3 className="text-lg font-semibold text-slate-800">{userName}</h3>
            <span className="text-xs text-slate-400">{data.total} {t('messages', lang)}</span>
          </div>
          <Button variant="destructive" size="sm" onClick={() => handleDelete(selectedUser)} data-testid="delete-user-chat-btn">
            <Trash2 className="w-3.5 h-3.5 mr-1" />{t('deleteUser', lang)}
          </Button>
        </div>
        <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
          {data.messages?.slice().reverse().map((msg, i) => (
            <div key={i} className="space-y-2" data-testid={`chat-message-${i}`}>
              <div className="flex justify-end">
                <div className="max-w-[80%] bg-blue-500 text-white rounded-2xl rounded-br-sm px-4 py-2.5 text-sm">
                  {msg.message}
                  <div className="text-[10px] text-blue-100 mt-1 text-left">{timeAgo(msg.timestamp, lang)}</div>
                </div>
              </div>
              <div className="flex justify-start">
                <div className="max-w-[80%] bg-slate-100 text-slate-700 rounded-2xl rounded-bl-sm px-4 py-2.5 text-sm whitespace-pre-wrap">
                  {msg.response}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Users list view
  const filteredUsers = data?.users?.filter(u =>
    !search || u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase())
  ) || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <MessageSquare className="w-5 h-5 text-blue-500" />
          <h2 className="text-xl font-bold text-slate-800">{t('title', lang)}</h2>
          <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full font-medium">{data?.total || 0} {t('totalMessages', lang)}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => fetchData()} data-testid="refresh-chat-btn"><RefreshCw className="w-3.5 h-3.5" /></Button>
          {data?.total > 0 && <Button variant="destructive" size="sm" onClick={() => handleDelete()} data-testid="delete-all-chat-btn"><Trash2 className="w-3.5 h-3.5 mr-1" />{t('deleteAll', lang)}</Button>}
        </div>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder={t('search', lang)}
          className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-testid="chat-search-input" />
      </div>

      {filteredUsers.length === 0 ? (
        <div className="text-center py-12 text-slate-400">{t('noMessages', lang)}</div>
      ) : (
        <div className="space-y-2">
          {filteredUsers.map((u, i) => (
            <div key={i} onClick={() => openUser(u.user_id)}
              className="flex items-center justify-between p-4 bg-white border border-slate-100 rounded-xl hover:border-blue-200 hover:shadow-sm cursor-pointer transition-all"
              data-testid={`chat-user-${i}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-800 text-sm">{u.name}</p>
                  <p className="text-xs text-slate-400">{u.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="bg-slate-100 text-slate-600 text-xs px-2.5 py-1 rounded-full font-medium">{u.message_count} {t('messages', lang)}</span>
                <span className="text-xs text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" />{timeAgo(u.last_message, lang)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChatMessagesManagement;
